/**
 * Ayudantes de texto de la libreta de destinatarios (Favoritos / Recientes).
 *
 * Lo que hay aquí no sale de suponer cómo funciona el buscador: sale de leer
 * `[accounts].[GetFavoriteAndRecentDestinations]` el 2026-09-12 durante la
 * pata 3 de Innovus. Los tres hechos que mandan:
 *
 *  - el SP descarta cualquier token que contenga un guion
 *    (`CHARINDEX('-', value) = 0`), así que "V-12345678" llegaba como un token
 *    que tiraba a la basura, se quedaba sin tokens y devolvía la libreta entera
 *    sin filtrar;
 *  - descarta los tokens de un solo carácter (`LEN(RTRIM(LTRIM(value))) > 1`);
 *  - une los tokens con OR, no con AND
 *    (`STRING_AGG('"' + value + '*"', ' OR ')`).
 *
 * Ese último es el que obliga a `serverQueryStillCovers`: agregar una palabra
 * AMPLÍA la respuesta del servidor, no la reduce.
 */

const COMBINING_MARKS = /[̀-ͯ]/g

// El guion no es un carácter reservado del full text, pero el SP tira el token
// que lo contenga, así que se parte en espacio igual que los reservados.
const FULLTEXT_RESERVED = /["'()[\]{}~*-]/g
const SEARCH_PUNCTUATION = /[-.\s]/g

/**
 * Se aplica igual a la consulta y a cada valor comparado, para que el filtro
 * del cliente no distinga acentos ni mayúsculas. La descomposición NFD lleva
 * "ñ" a "n" + U+0303 y al quitar las marcas queda una "n" pelada, así que
 * "peña" también encuentra "pena". Se acepta.
 */
export const normalizeSearchText = (value: string): string =>
  (value ?? '').normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase()

/**
 * Puerto de `sanitize_fulltext_query` de Django (pata 4). Protege al predicado
 * CONTAINS de los caracteres que lo harían reventar. Los reservados se
 * REEMPLAZAN por un espacio, nunca se borran: así "O'Brien" queda "O Brien",
 * que es lo que hace el separador de palabras, y no el "OBrien" que pegaría
 * dos tokens en uno.
 */
export const sanitizeFulltextQuery = (search: string): string =>
  (search ?? '').replace(FULLTEXT_RESERVED, ' ').replace(/\s+/g, ' ').trim()

/**
 * Identificaciones y teléfonos se comparan sin puntuación de los dos lados,
 * para que el "V-12.345.678" que el usuario lee en pantalla encuentre igual si
 * escribe "v-12345678", "V12345678" o "12.345.678".
 */
export const stripSearchPunctuation = (value: string): string =>
  (value ?? '').replace(SEARCH_PUNCTUATION, '')

/**
 * La forma exacta que se compara contra `serverQuery` para decidir si una
 * búsqueda de libreta completa todavía cubre lo que hay en el campo. Sanear
 * primero evita que un espacio de más rompa la comparación.
 */
export const toServerQueryForm = (search: string): string =>
  normalizeSearchText(sanitizeFulltextQuery(search))

/**
 * Los tokens que el SP va a buscar de verdad. Descarta los de un carácter, y
 * cuando no sobrevive ninguno abandona la búsqueda y devuelve la libreta
 * capada tal cual: una consulta sin tokens es una llamada cara que responde
 * con lo que ya teníamos en caché.
 */
export const serverSearchTokens = (serverQueryForm: string): string[] =>
  serverQueryForm.split(' ').filter((token) => token.length > 1)

/**
 * ¿Un resultado de libreta completa traído para `previous` sigue sirviendo
 * para `next`?
 *
 * Como el SP une con OR, AGREGAR un token AMPLÍA la respuesta: un resultado
 * traído para "mar" no tiene lo que sólo encuentra "lopez", y reusarlo haría
 * que el desplegable dijera "Sin coincidencias en su libreta" de alguien que
 * sí está. Alargar un token, en cambio, la reduce — `"maria*"` está contenido
 * en `"mar*"` — y eso sí sigue cubierto.
 */
export const serverQueryStillCovers = (
  previous: string,
  next: string
): boolean => {
  const before = serverSearchTokens(previous)
  const after = serverSearchTokens(next)

  if (before.length === 0 || before.length !== after.length) return false

  return before.every((token, index) => after[index].startsWith(token))
}
